import * as React from 'react'

import styled from '@emotion/styled'
import debounce from 'lodash.debounce'
import { useFormContext, ValidateResult } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  ArcadeFieldLabel,
  CashtagInput as ArcadeCashtagInput,
  FieldLabel,
  Input,
  LoadingIcon,
  PaddingInset,
} from '@cash-design-system/react'
import { CashtagStatus } from '@cash-web/protos/squareup/franklin/api/cashtag_status.pb'
import { GetCashtagStatusResponse } from '@cash-web/protos/squareup/franklin/app/cashtag.pb'
import { getCashtagStatus } from '@cash-web/shared-data-access-api/api'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-feature-flags'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { containsAnAlphaCharacter, isAlphanumeric } from '@cash-web/shared-util-validation'

import { FieldController } from '../FieldController/FieldController'
import { FormInlineError } from '../FormInlineError'
import { FormInputWrapper } from '../FormInputWrapper'

const LoadingWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xs};
  max-height: ${props => props.theme.pxToRem(17)};
`

const HintText = styled.div`
  color: ${props => props.theme.colors.secondaryLabel};
  ${props => props.theme.typography.helpText};
  margin-top: ${({ theme }) => theme.spacing.xs};
`

const CashtagWrapper = styled.span`
  position: relative;
  display: flex;
`

const StyledInput = styled(Input)<{ numPrefixChars?: number }>`
  width: 100%;
  ${props => props.numPrefixChars && `padding-left: ${props.numPrefixChars + 1}rem;`}
`

const HelpTextWrapper = styled(PaddingInset)`
  min-height: 1.5rem;
`

const CashtagPrefix = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-left: 1.375rem;
  height: 100%;
`

type Props = React.InputHTMLAttributes<HTMLInputElement> & {
  currencySymbol: string
  name: string
  showLabel?: boolean
  inputPrefix?: string
  onGetCashStatus?: (status: GetCashtagStatusResponse) => void
}

const CashtagInputLabel = ({
  currencySymbol,
  showLabel,
  children,
}: {
  currencySymbol: string
  showLabel?: boolean
  children: React.ReactNode
}) => {
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  if (!showLabel) {
    return children
  }

  if (isArcadeMigrationEnabled) {
    return <ArcadeFieldLabel label={`${currencySymbol}cashtag`}>{children}</ArcadeFieldLabel>
  }

  return <FieldLabel label={`${currencySymbol}cashtag`}>{children}</FieldLabel>
}

export function CashtagInput({
  currencySymbol,
  inputPrefix,
  name,
  placeholder,
  onGetCashStatus,
  showLabel,
  ...props
}: Props): React.ReactElement {
  const {
    register,
    trigger,
    getValues,
    formState: { errors, isValidating },
  } = useFormContext()
  const flow = useFlowContext()
  const [hintText, setHintText] = React.useState('')
  const { t } = useTranslation()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceValidation = React.useCallback(
    debounce(async () => {
      await trigger(name)
      const value = getValues(name)
      setHintText(`cash.app/${currencySymbol}${value}`)
    }, 250),
    []
  )

  React.useEffect(() => {
    return () => {
      debounceValidation.cancel()
    }
  }, [debounceValidation])

  const handleOnChange = () => {
    // to prevent the UI from bouncing, once a user begins typing we still render
    // the UI, even if the help text string is empty
    debounceValidation()
  }

  const isCashtagAvailable = async (cashtag: string): Promise<ValidateResult> => {
    try {
      const response = await getCashtagStatus(
        { cashtag_candidate: cashtag },
        {
          clientScenario: flow.active?.clientScenario,
          flowToken: flow.active?.flowToken,
        }
      )
      if (response) {
        onGetCashStatus?.(response)
      }
      switch (response.cashtag_status) {
        case CashtagStatus.AVAILABLE:
          return true
        case CashtagStatus.UNAVAILABLE:
          return (
            response.failure_message ??
            t('{currencySymbol}{cashtag} is unavailable.', {
              context: 'Error text. server error message',
              currencySymbol,
              cashtag,
            })
          )
        case CashtagStatus.INVALID_CASHTAG:
          return (
            response.failure_message ??
            t('{currencySymbol}{cashtag} is not a valid {currencySymbol}cashtag.', {
              context: 'Error text. server error message',
              currencySymbol,
              cashtag,
            })
          )
        default:
          return t('There was an error verifying {currencySymbol}{cashtag}.', {
            context: 'Error text. server error message',
            currencySymbol,
            cashtag,
          })
      }
    } catch {
      return t('There was an error verifying {currencySymbol}{cashtag}.', {
        context: 'Error text. server error message',
        currencySymbol,
        cashtag,
      })
    }
  }

  if (isArcadeMigrationEnabled) {
    return (
      <FormInputWrapper>
        <CashtagInputLabel currencySymbol={currencySymbol} showLabel={showLabel}>
          <FieldController
            FieldComponent={ArcadeCashtagInput}
            componentProps={{
              autoFocus: true,
              currencySymbol: currencySymbol as '$' | '£' | '€',
              placeholder: placeholder || t('$YourCashTag', { context: 'cashtag placeholder' }),
              helpText: hintText,
              onChange: handleOnChange,
            }}
            name={name}
            defaultValue={props.value || props.defaultValue}
            rules={{
              required: {
                value: true,
                message: t('Enter your {currencySymbol}cashtag.', {
                  context: 'error message for form field when missing a cashtag',
                  currencySymbol,
                }),
              },
              minLength: {
                value: 3,
                message: t('{currencySymbol}cashtag is too short.', {
                  context: 'error message for form field when cashtag is too short',
                  currencySymbol,
                }),
              },
              maxLength: {
                value: 20,
                message: t('{currencySymbol}cashtag is too long.', {
                  context: 'error message for form field when cashtag is too long',
                  currencySymbol,
                }),
              },
              validate: {
                isAlphaNumeric: (value: string) =>
                  isAlphanumeric(value) ||
                  t('{currencySymbol}cashtags can only contain letters and numbers.', {
                    context: 'error message for form field when cashtag contains invalid characters',
                    currencySymbol,
                  }),
                containsAnAlphaCharacter: (value: string) =>
                  containsAnAlphaCharacter(value) ||
                  t('{currencySymbol}cashtags must contain letters.', {
                    context: 'error message for form field when cashtag contains non-letter characters',
                    currencySymbol,
                  }),
                isAvailable: (value: string) => isCashtagAvailable(value),
              },
            }}
          />
        </CashtagInputLabel>
      </FormInputWrapper>
    )
  }

  const cashTagText = errors[name]?.message ? (
    <FormInlineError id="cash-tag-error-msg">{errors[name].message}</FormInlineError>
  ) : (
    <HintText id="cash-tag-url">{hintText}</HintText>
  )

  return (
    <FormInputWrapper>
      <CashtagInputLabel currencySymbol={currencySymbol} showLabel={showLabel}>
        <CashtagWrapper>
          {inputPrefix && <CashtagPrefix data-testid="prefix">{inputPrefix}</CashtagPrefix>}
          <StyledInput
            aria-label="cashtag"
            aria-describedby="cash-tag-error-msg cash-tag-url"
            placeholder={placeholder}
            className={errors[name] ? 'error' : ''}
            spellCheck="false"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            autoFocus
            numPrefixChars={inputPrefix?.length}
            {...props}
            {...register(name, {
              required: {
                value: true,
                message: t('Enter your {currencySymbol}cashtag.', {
                  context: 'error message for form field when missing a cashtag',
                  currencySymbol,
                }),
              },
              minLength: {
                value: 3,
                message: t('{currencySymbol}cashtag is too short.', {
                  context: 'error message for form field when cashtag is too short',
                  currencySymbol,
                }),
              },
              maxLength: {
                value: 20,
                message: t('{currencySymbol}cashtag is too long.', {
                  context: 'error message for form field when cashtag is too long',
                  currencySymbol,
                }),
              },
              validate: {
                isAlphaNumeric: (value: string) =>
                  isAlphanumeric(value) ||
                  t('{currencySymbol}cashtags can only contain letters and numbers.', {
                    context: 'error message for form field when cashtag contains invalid characters',
                    currencySymbol,
                  }),
                containsAnAlphaCharacter: (value: string) =>
                  containsAnAlphaCharacter(value) ||
                  t('{currencySymbol}cashtags must contain letters.', {
                    context: 'error message for form field when cashtag contains non-letter characters',
                    currencySymbol,
                  }),
                isAvailable: (value: string) => isCashtagAvailable(value),
              },
              onChange: handleOnChange,
            })}
          />
        </CashtagWrapper>
      </CashtagInputLabel>

      <HelpTextWrapper data-testid="cashtag-blocker-help-text-wrapper">
        {isValidating ? (
          <LoadingWrapper>
            <LoadingIcon size={17} />
          </LoadingWrapper>
        ) : (
          cashTagText
        )}
      </HelpTextWrapper>
    </FormInputWrapper>
  )
}
